import React, { useState, useContext, useCallback, useEffect } from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleNav } from '../actions';
import ShiftsContext from '../Context/ShiftsContext';
import '../css/AdminNav.scss';

import {
    Board28Regular,
    CalendarRtl28Regular,
    Shifts28Regular,
    PersonQuestionMark24Regular,
    CalendarClock24Regular,
    ShiftsActivity24Regular,
    PeopleTeam28Regular,
    Chat24Regular,
    Clock28Regular,
    BrainCircuit24Regular,
    FormNew24Regular,
    DocumentSearch24Regular,
    TaskListSquareLtr24Regular,
    DataBarVertical24Regular,
    Settings28Regular,
    ChevronRight20Regular,
    PanelLeftContract24Regular,
    PanelLeftExpand24Regular,
} from '@fluentui/react-icons';

/* ────────────────────────────────────────────────────────────
   Data: nav groups, items, sub-items
   ──────────────────────────────────────────────────────────── */

const buildNavGroups = (modules) => {
    const groups = [
        // ── Top (no header) ──
        {
            header: null,
            items: [
                { key: 'home', label: 'Home', icon: <Board28Regular />, to: '/scheduler/overview', exact: true },
            ],
        },
        // ── Scheduling ──
        {
            header: 'Scheduling',
            visible: modules.ScheduleModule,
            items: [
                { key: 'schedule', label: 'Schedule', icon: <CalendarRtl28Regular />, action: 'schedulerClick' },
                {
                    key: 'shifts', label: 'Staffing Needs', icon: <CalendarClock24Regular />, to: '/scheduler/shifts',
                    subItems: [
                        { key: 'shifts-all', label: 'All Staffing Needs', to: '/scheduler/shifts' },
                        { key: 'shift-types', label: 'Shift Types', to: '/scheduler/shifts/shift-types' },
                        { key: 'shift-templates', label: 'Schedule Templates', to: '/scheduler/shifts/templates' },
                        { key: 'shift-alerts-create', label: 'Create Alert', to: '/scheduler/shifts/shiftalerts' },
                        { key: 'shift-alerts', label: 'Shift Alerts', to: '/scheduler/shifts/shiftalert/create' },
                    ],
                },
                {
                    key: 'requests', label: 'Requests', icon: <ShiftsActivity24Regular />, to: '/scheduler/requests',
                    subItems: [
                        { key: 'req-work', label: 'To Work', to: '/scheduler/requests' },
                        { key: 'req-pto', label: 'Time Off', to: '/scheduler/requests/pto' },
                        { key: 'req-swap', label: 'Swap Shift', to: '/scheduler/requests/swap' },
                        { key: 'req-availability', label: 'Availability Change', to: '/scheduler/requests/availability' },
                        { header: 'Availability' },
                        { key: 'req-users-avail', label: 'Team Availability', to: '/scheduler/requests/users-availability' },
                        { key: 'req-my-avail', label: 'My Availability', to: '/scheduler/requests/my-availability' },
                    ],
                },
            ],
        },
        // ── Team ──
        {
            header: 'Team',
            items: [
                { key: 'staff', label: 'Staff', icon: <PeopleTeam28Regular />, to: '/scheduler/users' },
                { key: 'messages', label: 'Messages', icon: <Chat24Regular />, to: '/chats' },
                ...(modules.TimesheetModule
                    ? [{ key: 'timesheets', label: 'Timesheets', icon: <Clock28Regular />, to: '/timesheets' }]
                    : []),
            ],
        },
        // ── Operations ──
        {
            header: 'Operations',
            items: [
                ...(modules.CoursesModule
                    ? [{ key: 'training', label: 'Training', icon: <BrainCircuit24Regular />, to: '/quiz/overview' }]
                    : []),
                ...(modules.FormsModule
                    ? [{ key: 'forms', label: 'Forms', icon: <FormNew24Regular />, to: '/forms' }]
                    : []),
                ...(modules.AuditsModule
                    ? [{ key: 'audits', label: 'Audits', icon: <DocumentSearch24Regular />, to: '/audits' }]
                    : []),
                ...(modules.TasksModule
                    ? [{ key: 'tasks', label: 'Tasks', icon: <TaskListSquareLtr24Regular />, to: '/tasks' }]
                    : []),
            ],
        },
        // ── Insights ──
        {
            header: 'Insights',
            visible: modules.ScheduleModule || modules.AuditsModule || modules.TasksModule,
            items: [
                {
                    key: 'reports', label: 'Reports', icon: <DataBarVertical24Regular />, to: '/reports',
                    subItems: [
                        { key: 'rpt-summary', label: 'Schedule Summary', to: '/reports' },
                        { key: 'rpt-role', label: 'Role Summary', to: '/reports/schedule/position-summary' },
                        { key: 'rpt-hours', label: 'Hours Scheduled', to: '/reports/schedule/hours-scheduled' },
                        { header: 'Course Reports' },
                        { key: 'rpt-quiz', label: 'Quiz Results', to: '/reports/quiz' },
                        { key: 'rpt-lessons', label: 'Lesson Results', to: '/reports/lessons' },
                        { header: 'Audit Reports' },
                        { key: 'rpt-audit-summary', label: 'Summary', to: '/reports/audit/summary' },
                        { key: 'rpt-audit-scores', label: 'Scores', to: '/reports/audit/scores' },
                        { key: 'rpt-audit-comp', label: 'Completions', to: '/reports/audit/completions' },
                    ],
                },
            ],
        },
    ];

    // Filter out empty groups
    return groups.filter(g => {
        if (g.visible === false) return false;
        return g.items.length > 0;
    });
};

/* Settings (pinned at bottom) */
const settingsItem = {
    key: 'settings', label: 'Settings', icon: <Settings28Regular />, to: '/settings',
    subItems: [
        { key: 'set-company', label: 'Company Details', to: '/settings' },
        { key: 'set-billing', label: 'Plan & Billing', to: '/settings/invoices' },
        { key: 'set-roles', label: 'Departments & Roles', to: '/settings/roles' },
        { key: 'set-modules', label: 'Modules', to: '/settings/modules' },
        { header: 'Feedback' },
        { key: 'set-feedback', label: 'Shift Feedback', to: '/settings/timesheets' },
        { header: 'Timesheets' },
        { key: 'set-time', label: 'Time Tracking', to: '/settings/timesheets' },
        { key: 'set-overtime', label: 'Overtime & Breaks', to: '/settings/overtime' },
        { key: 'set-enforce', label: 'Enforcements', to: '/settings/enforcements' },
    ],
};

/* ────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────── */

/** Check if a path is active (exact or prefix) */
function isPathActive(pathname, to, exact) {
    if (!to) return false;
    if (exact) return pathname === to;
    return pathname === to || pathname.startsWith(to + '/');
}

/** Check if any sub-item route is active */
function isSubActive(pathname, subItems) {
    if (!subItems) return false;
    return subItems.some(s => s.to && isPathActive(pathname, s.to, false));
}

/* ────────────────────────────────────────────────────────────
   Sub-components
   ──────────────────────────────────────────────────────────── */

function NavSubItems({ subItems, pathname, open }) {
    return (
        <ul className={`admin-nav__subitems ${open ? 'admin-nav__subitems--open' : ''}`}>
            {subItems.map((sub, idx) => {
                if (sub.header) {
                    return <li key={`hdr-${idx}`} className="admin-nav__sub-header">{sub.header}</li>;
                }
                const active = isPathActive(pathname, sub.to, false);
                return (
                    <li key={sub.key} className="admin-nav__subitem">
                        <NavLink
                            to={sub.to}
                            className={`admin-nav__subitem-link ${active ? 'admin-nav__subitem-link--active' : ''}`}
                        >
                            {sub.label}
                        </NavLink>
                    </li>
                );
            })}
        </ul>
    );
}

function NavItem({ item, pathname, expanded, onToggle, onSchedulerClick }) {
    const hasChildren = item.subItems && item.subItems.length > 0;
    const isAction = item.action === 'schedulerClick';

    const active = isAction
        ? pathname.indexOf('scheduler/schedule') > -1
        : isPathActive(pathname, item.to, item.exact) || isSubActive(pathname, item.subItems);

    const handleClick = useCallback((e) => {
        if (isAction) {
            e.preventDefault();
            onSchedulerClick();
            return;
        }
        if (hasChildren) {
            e.preventDefault();
            onToggle(item.key);
        }
    }, [isAction, hasChildren, item.key, onToggle, onSchedulerClick]);

    const rowContent = (
        <>
            <span className="admin-nav__icon-wrap">{item.icon}</span>
            <span className="admin-nav__label">{item.label}</span>
            {hasChildren && (
                <span className={`admin-nav__chevron ${expanded ? 'admin-nav__chevron--open' : ''}`}>
                    <ChevronRight20Regular />
                </span>
            )}
        </>
    );

    return (
        <li className="admin-nav__item">
            {isAction ? (
                <a
                    href="#"
                    className={`admin-nav__item-row ${active ? 'admin-nav__item-row--active' : ''}`}
                    onClick={handleClick}
                >
                    {rowContent}
                </a>
            ) : hasChildren ? (
                <a
                    href="#"
                    className={`admin-nav__item-row ${active ? 'admin-nav__item-row--active' : ''}`}
                    onClick={handleClick}
                >
                    {rowContent}
                </a>
            ) : (
                <NavLink
                    to={item.to}
                    exact={item.exact}
                    className={`admin-nav__item-row ${active ? 'admin-nav__item-row--active' : ''}`}
                >
                    {rowContent}
                </NavLink>
            )}
            {hasChildren && (
                <NavSubItems subItems={item.subItems} pathname={pathname} open={expanded} />
            )}
        </li>
    );
}

/* ────────────────────────────────────────────────────────────
   Main component
   ──────────────────────────────────────────────────────────── */

export default function AdminNav({ modules, logoSrc, clientName }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const pathname = location.pathname;

    const closeNav = useSelector(state => state.rootReducer.closeNav);
    const collapsed = !!closeNav;

    // Expanded sections (supports multiple open simultaneously)
    const [expandedKeys, setExpandedKeys] = useState(() => {
        // Auto-expand any section whose sub-route is currently active
        const initial = new Set();
        const allItems = [...buildNavGroups(modules).flatMap(g => g.items), settingsItem];
        allItems.forEach(item => {
            if (item.subItems && isSubActive(pathname, item.subItems)) {
                initial.add(item.key);
            }
        });
        return initial;
    });

    // Auto-expand sections when route changes
    useEffect(() => {
        const allItems = [...buildNavGroups(modules).flatMap(g => g.items), settingsItem];
        allItems.forEach(item => {
            if (item.subItems && isSubActive(pathname, item.subItems)) {
                setExpandedKeys(prev => {
                    if (prev.has(item.key)) return prev;
                    const next = new Set(prev);
                    next.add(item.key);
                    return next;
                });
            }
        });
    }, [pathname]);

    const toggleSection = useCallback((key) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const handleCollapse = useCallback(() => {
        dispatch(toggleNav(!closeNav));
    }, [dispatch, closeNav]);

    // Schedule click handler from ShiftsContext
    const shiftsCtx = useContext(ShiftsContext);
    const schedulerClick = useCallback(() => {
        if (!shiftsCtx) return;
        const { scheduleType, refreshVisibleData, filteredShifts, hardRefreshShifts } = shiftsCtx;
        if (filteredShifts && filteredShifts.length === 0) {
            hardRefreshShifts();
        }
        history.push('/scheduler/schedule/' + scheduleType);
        refreshVisibleData(scheduleType);
    }, [shiftsCtx, history]);

    const groups = buildNavGroups(modules);

    return (
        <nav className={`admin-sidebar ${collapsed ? 'admin-sidebar--collapsed' : ''}`}>
            {/* ── Brand: logo + client name (replaces top-nav logo when new nav is active) ── */}
            <div className="admin-nav__brand">
                <a href="/" className="admin-nav__brand-link">
                    <img
                        className="admin-nav__brand-logo"
                        src={logoSrc || "https://teamsly-cdn-cuc0cvgwakbtarf9.z01.azurefd.net/images/circle-100.png"}
                        alt=""
                    />
                    <span className="admin-nav__brand-text">{clientName}</span>
                </a>
            </div>
            {/* ── Scrollable main area ── */}
            <div className="admin-nav__main">
                {groups.map((group, gi) => (
                    <div key={gi} className="admin-nav__group">
                        {group.header && (
                            <div className="admin-nav__group-header">{group.header}</div>
                        )}
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {group.items.map(item => (
                                <NavItem
                                    key={item.key}
                                    item={item}
                                    pathname={pathname}
                                    expanded={expandedKeys.has(item.key)}
                                    onToggle={toggleSection}
                                    onSchedulerClick={schedulerClick}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {/* ── Bottom pinned: Settings + Collapse ── */}
            <div className="admin-nav__bottom">
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    <NavItem
                        item={settingsItem}
                        pathname={pathname}
                        expanded={expandedKeys.has(settingsItem.key)}
                        onToggle={toggleSection}
                        onSchedulerClick={schedulerClick}
                    />
                </ul>
                <button className="admin-nav__collapse-btn" onClick={handleCollapse}>
                    {collapsed ? <PanelLeftExpand24Regular /> : <PanelLeftContract24Regular />}
                    <span className="admin-nav__collapse-text">
                        {collapsed ? 'Expand' : 'Collapse'}
                    </span>
                </button>
            </div>
        </nav>
    );
}
